import type { CycleCallback } from '@/components/ui/Timeline';
import { dayjsToSeconds } from '@/components/ui/Timeline/utils/time-conversions';

const MILLISECONDS_IN_ONE_SECOND = 1000;
const FRAMES_PER_SECOND = 25;
const STORED_VIDEO_DURATION_PRECISION_IN_SECONDS = 1;
const VIDEO_END_TOLERANCE_IN_MILLISECONDS = STORED_VIDEO_DURATION_PRECISION_IN_SECONDS * MILLISECONDS_IN_ONE_SECOND;
const CONTIGUITY_TOLERANCE_IN_MILLISECONDS = MILLISECONDS_IN_ONE_SECOND / FRAMES_PER_SECOND;
const COVERAGE_START = 0;

export const STRETCH_ISSUE = {
	EXCEEDS_VIDEO: 'exceeds-video',
	INCOMPLETE_COVERAGE: 'incomplete-coverage'
} as const;

export type StretchIssue = (typeof STRETCH_ISSUE)[keyof typeof STRETCH_ISSUE];

export const STRETCH_ISSUE_MESSAGE: Record<StretchIssue, string> = {
	[STRETCH_ISSUE.EXCEEDS_VIDEO]: 'The identified stretches exceed the total video time. Fix it to proceed.',
	[STRETCH_ISSUE.INCOMPLETE_COVERAGE]:
		'There are unidentified video stretches. Complete the identification to proceed.'
};

interface CoverageRange {
	start: number;
	end: number;
}

interface FindStretchIssueParams {
	stretches: CycleCallback[];
	videoDuration: number;
}

function toMilliseconds(seconds: number): number {
	const result = Math.round(seconds * MILLISECONDS_IN_ONE_SECOND);

	return result;
}

export function isOutOfVideoRange(endInSeconds: number, videoDuration: number): boolean {
	const endInMilliseconds = toMilliseconds(endInSeconds);
	const limitInMilliseconds = toMilliseconds(videoDuration) + VIDEO_END_TOLERANCE_IN_MILLISECONDS;
	const result = endInMilliseconds > limitInMilliseconds;

	return result;
}

function toSortedRanges(stretches: CycleCallback[]): CoverageRange[] {
	const ranges = stretches.map((stretch) => ({
		start: dayjsToSeconds(stretch.start_time),
		end: dayjsToSeconds(stretch.end_time)
	}));
	const result = ranges.sort((first, second) => first.start - second.start);

	return result;
}

function isFullyCovered(ranges: CoverageRange[], videoDuration: number): boolean {
	let covered = COVERAGE_START;

	for (const range of ranges) {
		const startInMilliseconds = toMilliseconds(range.start);
		const reachedInMilliseconds = toMilliseconds(covered) + CONTIGUITY_TOLERANCE_IN_MILLISECONDS;

		if (startInMilliseconds > reachedInMilliseconds) {
			return false;
		}

		covered = Math.max(covered, range.end);
	}

	const coveredInMilliseconds = toMilliseconds(covered);
	const requiredInMilliseconds = toMilliseconds(videoDuration) - CONTIGUITY_TOLERANCE_IN_MILLISECONDS;
	const result = coveredInMilliseconds >= requiredInMilliseconds;

	return result;
}

export function findStretchIssue({ stretches, videoDuration }: FindStretchIssueParams): StretchIssue | null {
	if (!videoDuration) {
		return null;
	}

	const ranges = toSortedRanges(stretches);
	const exceedsVideo = ranges.some((range) => isOutOfVideoRange(range.end, videoDuration));

	if (exceedsVideo) {
		return STRETCH_ISSUE.EXCEEDS_VIDEO;
	}

	if (!isFullyCovered(ranges, videoDuration)) {
		return STRETCH_ISSUE.INCOMPLETE_COVERAGE;
	}

	return null;
}
